import { useEffect, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { PageHeader, Loading, Empty } from "../components/Layout";
import { Network as NetIcon, Search, Users, Plus, Trash2, Sparkles } from "lucide-react";

const TIER = { team: { label: "Sales team", color: "#2F7A3E" }, wider: { label: "Wider · LinkedIn", color: "#7A5CB2" } };
const SRC_LABEL = { mailbox: "Mailbox", linkedin: "LinkedIn", crm: "CRM", vendor: "Vendor", whatsapp: "WhatsApp", botim: "Botim" };

function MyNetwork() {
  const [optIn, setOptIn] = useState(false);
  const [rows, setRows] = useState(null);
  const [f, setF] = useState({ name: "", company: "", title: "", linkedin_url: "", tier: "team" });
  const load = () => Promise.all([api.get("/network/opt-in"), api.get("/network/connections")]).then(([o, c]) => { setOptIn(o.data.opted_in); setRows(c.data); });
  useEffect(() => { load(); }, []);
  const toggle = async () => { const v = !optIn; await api.post("/network/opt-in", { opt_in: v }); setOptIn(v); toast.success(v ? "Opted in — your wider LinkedIn connections are now discoverable" : "Opted out"); };
  const add = async () => {
    if (!f.name.trim()) { toast.error("Name required"); return; }
    try { await api.post("/network/connections", f); setF({ name: "", company: "", title: "", linkedin_url: "", tier: "team" }); load(); toast.success("Connection added"); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const del = async (c) => { try { await api.delete(`/network/connections/${c.id}`); load(); } catch (e) { toast.error("Failed"); } };
  const bulkUpload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    e.target.value = "";
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post("/network/connections/bulk-file", fd);
      toast.success(`${data.added} wider connections imported · ${data.skipped} skipped`); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Import failed"); }
  };
  const inp = "border border-[#DFDAD0] px-2 py-1.5 text-sm outline-none focus:border-[#1C2530]";
  return (
    <div className="border border-[#DFDAD0] bg-white p-5" data-testid="my-network">
      <div className="flex items-center justify-between mb-3">
        <div className="font-mono text-[10px] uppercase tracking-wider text-[#1C2530]/60">My network · add LinkedIn connections</div>
        <div className="flex items-center gap-2">
          <label data-testid="conn-bulk-label" title="Upload LinkedIn Connections.csv or the full export .zip (imported as Wider tier)"
            className="flex items-center gap-1.5 px-3 py-1.5 font-mono text-[10px] uppercase border border-[#DFDAD0] text-[#1C2530]/70 hover:bg-[#EFECE5] cursor-pointer">
            <Users className="w-3.5 h-3.5" /> Import LinkedIn CSV/ZIP → Wider
            <input type="file" accept=".csv,.zip,text/csv,application/zip" onChange={bulkUpload} data-testid="conn-bulk-input" className="hidden" />
          </label>
          <button onClick={toggle} data-testid="network-optin-toggle"
            className={`flex items-center gap-1.5 px-3 py-1.5 font-mono text-[10px] uppercase border ${optIn ? "bg-[#7A5CB2] text-white border-[#7A5CB2]" : "border-[#DFDAD0] text-[#1C2530]/70 hover:bg-[#EFECE5]"}`}>
            <Users className="w-3.5 h-3.5" /> Wider-company opt-in: {optIn ? "ON" : "OFF"}
          </button>
        </div>
      </div>
      <div className="mb-3 font-mono text-[9px] text-[#1C2530]/45">Bulk CSV import adds to the Wider (opt-in, LinkedIn-only) tier — discoverable to the team only while your opt-in is ON.</div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-3">
        <input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Name *" data-testid="conn-name" className={inp} />
        <input value={f.company} onChange={(e) => setF({ ...f, company: e.target.value })} placeholder="Company" data-testid="conn-company" className={inp} />
        <input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} placeholder="Title" className={inp} />
        <select value={f.tier} onChange={(e) => setF({ ...f, tier: e.target.value })} data-testid="conn-tier" className={inp + " bg-white"}>
          <option value="team">Team tier</option>
          <option value="wider">Wider (opt-in, LinkedIn-only)</option>
        </select>
        <button onClick={add} data-testid="conn-add" className="flex items-center justify-center gap-1 bg-[#1C2530] text-[#F5F3EE] px-3 py-1.5 text-xs hover:bg-[#2a3644]"><Plus className="w-3.5 h-3.5" /> Add</button>
      </div>
      {!rows ? <Loading /> : rows.length === 0 ? <div className="font-mono text-xs text-[#1C2530]/40">NO_CONNECTIONS</div> : (
        <div className="space-y-1">
          {rows.map((c) => (
            <div key={c.id} className="flex items-center justify-between text-sm py-1 border-b border-[#DFDAD0] last:border-0" data-testid={`conn-row-${c.id}`}>
              <span className="text-[#1C2530]">{c.name} <span className="text-[#1C2530]/50">· {c.company || "—"} · {c.title || "—"}</span> <span className="font-mono text-[9px] uppercase" style={{ color: TIER[c.tier].color }}>{TIER[c.tier].label}</span></span>
              <button onClick={() => del(c)} className="text-[#B23A2E] p-1 hover:bg-[#B23A2E]/5"><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Network() {
  const [q, setQ] = useState("");
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const search = async () => {
    setBusy(true);
    try { const { data } = await api.post("/network/search", { q }); setRes(data.results); }
    catch (e) { toast.error(e.response?.data?.detail || "Search failed"); } finally { setBusy(false); }
  };
  return (
    <>
      <PageHeader eyebrow="WARM-INTRO PATHS · TEAM-WIDE" title="Warm Network Search" hint="Find who on the team already knows someone at a target account — via connected inboxes and LinkedIn — so outreach starts warm instead of cold." />
      <div className="p-8 space-y-5" data-testid="network-page">
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#1C2530]/40" />
            <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && search()}
              placeholder="Who do we know at a bank in Dubai? … natural language" data-testid="network-search-input"
              className="w-full border border-[#DFDAD0] pl-9 pr-3 py-2.5 text-sm outline-none focus:border-[#1C2530]" />
          </div>
          <button onClick={search} disabled={busy} data-testid="network-search-btn" className="flex items-center gap-2 bg-[#E50914] text-white px-5 py-2.5 text-sm font-medium hover:bg-[#c40810] disabled:opacity-50"><Sparkles className="w-4 h-4" /> {busy ? "Searching…" : "Search"}</button>
        </div>

        <MyNetwork />

        {res === null ? null : res.length === 0 ? <Empty label="NO_WARM_PATHS_FOUND" /> : (
          <div className="border border-[#DFDAD0] bg-white overflow-hidden" data-testid="network-results">
            <div className="px-4 py-2.5 bg-[#EFECE5] font-mono text-[10px] uppercase tracking-wider text-[#1C2530]/60">{res.length} warm path(s)</div>
            {res.map((r, i) => {
              const t = TIER[r.tier];
              return (
                <div key={i} className="flex items-center justify-between px-4 py-3 border-t border-[#DFDAD0]" data-testid={`network-result-${i}`}>
                  <div>
                    <div className="text-sm font-medium text-[#1C2530]">{r.name} <span className="text-[#1C2530]/50 font-normal">· {r.company} · {r.title || "—"}</span></div>
                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                      <span className="font-mono text-[9px] uppercase px-1.5 py-0.5" style={{ color: t.color, background: `${t.color}15` }}>{t.label}</span>
                      {r.sources.map((s) => <span key={s} className="font-mono text-[9px] uppercase text-[#1C2530]/40">{SRC_LABEL[s] || s}</span>)}
                      <span className="font-mono text-[9px] text-[#1C2530]/40">score {r.score}</span>
                    </div>
                    <div className="text-xs text-[#1C2530]/70 mt-1" data-testid={`network-connectors-${i}`}>
                      {r.connectors.length ? <>Warm intro via <b>{r.connectors.join(", ")}</b></> : <span className="text-[#1C2530]/40">No internal connector recorded</span>}
                    </div>
                  </div>
                  <a href={r.linkedin_url} target="_blank" rel="noreferrer" data-testid={`network-linkedin-${i}`} className="flex items-center gap-1 border border-[#3E8F99] text-[#3E8F99] px-2 py-1 font-mono text-[9px] uppercase hover:bg-[#3E8F99]/10">LinkedIn</a>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
}
